import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Menu,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Stack,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Event as EventIcon,
  Star as StarIcon,
  MusicNote as MusicIcon,
  School as WorkshopIcon,
  Public as PublicIcon,
  Unpublished as UnpublishedIcon,
  CalendarToday as CalendarIcon,
  Place as PlaceIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';

const Events = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'arangetram',
    event_date: '',
    venue: '',
    is_published: true
  });

  const eventTypes = [
    { value: 'arangetram', label: 'Arangetram', icon: <StarIcon /> },
    { value: 'annual_day', label: 'Annual Day', icon: <EventIcon /> },
    { value: 'performance', label: 'Performance', icon: <MusicIcon /> },
    { value: 'workshop', label: 'Workshop', icon: <WorkshopIcon /> },
    { value: 'other', label: 'Other', icon: <EventIcon /> }
  ];

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, activeTab]);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/events/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data.events || []);
    } catch (error) {
      showSnackbar('Error fetching events', 'error');
    }
  };

  const filterEvents = () => {
    if (activeTab === 'all') {
      setFilteredEvents(events);
    } else {
      setFilteredEvents(events.filter(event => event.event_type === activeTab));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        title: event.title || '',
        description: event.description || '',
        event_type: event.event_type || 'arangetram',
        event_date: event.event_date ? event.event_date.split('T')[0] : '',
        venue: event.venue || '',
        is_published: event.is_published !== undefined ? event.is_published : true
      });
    } else {
      setEditingEvent(null);
      setFormData({
        title: '',
        description: '',
        event_type: 'arangetram',
        event_date: '',
        venue: '',
        is_published: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_published' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.title || !formData.event_date) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      const token = localStorage.getItem('access_token');
      const url = editingEvent
        ? `/api/events/${editingEvent.id}`
        : '/api/events/';
      
      const method = editingEvent ? 'put' : 'post';

      await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showSnackbar(
        editingEvent ? 'Event updated successfully' : 'Event created successfully',
        'success'
      );
      
      handleCloseDialog();
      fetchEvents();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving event', 'error');
    }
  };

  const handleDeleteEvent = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/events/${selectedEvent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showSnackbar('Event deleted successfully', 'success');
      handleCloseMenu();
      fetchEvents();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting event', 'error');
    }
  };

  const handleMenuOpen = (event, eventData) => {
    setAnchorEl(event.currentTarget);
    setSelectedEvent(eventData);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getEventTypeIcon = (type) => {
    const eventType = eventTypes.find(t => t.value === type);
    return eventType ? eventType.icon : <EventIcon />;
  };

  const getEventTypeLabel = (type) => {
    const eventType = eventTypes.find(t => t.value === type);
    return eventType ? eventType.label : type;
  };

  const getEventStats = () => {
    const stats = {
      total: events.length,
      published: events.filter(e => e.is_published).length,
      upcoming: events.filter(e => new Date(e.event_date) > new Date()).length
    };
    
    eventTypes.forEach(type => {
      stats[type.value] = events.filter(e => e.event_type === type.value).length;
    });
    
    return stats;
  };

  const stats = getEventStats();

  return (
    <AdminLayout>
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          mb: 3,
          gap: isMobile ? 2 : 0
        }}>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
            Events & Performances
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            fullWidth={isMobile}
          >
            Add Event
          </Button>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent sx={{ py: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                      {stats.total}
                    </Typography>
                    <Typography variant={isMobile ? "caption" : "body2"}>
                      Total Events
                    </Typography>
                  </Box>
                  <EventIcon sx={{ fontSize: isMobile ? 32 : 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
              <CardContent sx={{ py: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                      {stats.published}
                    </Typography>
                    <Typography variant={isMobile ? "caption" : "body2"}>
                      Published
                    </Typography>
                  </Box>
                  <PublicIcon sx={{ fontSize: isMobile ? 32 : 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
              <CardContent sx={{ py: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                      {stats.upcoming}
                    </Typography>
                    <Typography variant={isMobile ? "caption" : "body2"}>
                      Upcoming
                    </Typography>
                  </Box>
                  <StarIcon sx={{ fontSize: isMobile ? 32 : 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <CardContent sx={{ py: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                      {stats.arangetram}
                    </Typography>
                    <Typography variant={isMobile ? "caption" : "body2"}>
                      Arangetrams
                    </Typography>
                  </Box>
                  <StarIcon sx={{ fontSize: isMobile ? 32 : 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filter Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All Events" value="all" />
            {eventTypes.map(type => (
              <Tab
                key={type.value}
                label={isMobile ? type.label : `${type.label} (${stats[type.value] || 0})`}
                value={type.value}
                icon={!isMobile ? type.icon : null}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Card>

        {/* Events List - Desktop Table / Mobile Cards */}
        {isMobile ? (
          // Mobile Card View
          <Box>
            {filteredEvents.length === 0 ? (
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" align="center" py={4}>
                    No events found. Click "Add Event" to create one.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Stack spacing={2}>
                {filteredEvents.map((event) => (
                  <Card key={event.id} sx={{ 
                    borderLeft: 4, 
                    borderColor: event.is_published ? 'success.main' : 'grey.300'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            {event.title}
                          </Typography>
                          {event.is_published ? (
                            <Chip
                              label="Published"
                              color="success"
                              size="small"
                              icon={<PublicIcon />}
                              sx={{ mb: 1 }}
                            />
                          ) : (
                            <Chip
                              label="Draft"
                              color="default"
                              size="small"
                              icon={<UnpublishedIcon />}
                              sx={{ mb: 1 }}
                            />
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, event)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>

                      {event.description && (
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {event.description}
                        </Typography>
                      )}

                      <Divider sx={{ my: 1.5 }} />

                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getEventTypeIcon(event.event_type)}
                          <Typography variant="body2" color="text.secondary">
                            {getEventTypeLabel(event.event_type)}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {new Date(event.event_date).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                          <Chip
                            label={new Date(event.event_date) > new Date() ? 'Upcoming' : 'Past'}
                            size="small"
                            color={new Date(event.event_date) > new Date() ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </Box>

                        {event.venue && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PlaceIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {event.venue}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        ) : (
          // Desktop Table View
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {activeTab === 'all' ? 'All Events' : getEventTypeLabel(activeTab)}
              </Typography>
              {isMobile ? (
                /* Mobile Card View */
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {filteredEvents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" py={3}>
                      No events found. Click "Add Event" to create one.
                    </Typography>
                  ) : (
                    filteredEvents.map((event) => (
                      <Card 
                        key={event.id} 
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
                          border: '1px solid',
                          borderColor: 'rgba(212, 175, 55, 0.2)',
                          boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 4px 16px rgba(212, 175, 55, 0.25)',
                            transform: 'translateY(-2px)',
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
                          },
                        }}
                      >
                        <CardContent sx={{ pl: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2C2416', fontSize: '1.1rem' }}>
                                {event.title}
                              </Typography>
                              {event.description && (
                                <Typography variant="caption" sx={{ color: '#8B7355', fontSize: '0.8rem' }}>
                                  {event.description.substring(0, 50)}
                                  {event.description.length > 50 ? '...' : ''}
                                </Typography>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, event)}
                              sx={{ color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212, 175, 55, 0.1)' } }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Box>
                          
                          <Divider sx={{ my: 1.5, borderColor: 'rgba(212, 175, 55, 0.15)' }} />
                          
                          <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ color: '#D4AF37' }}>
                                {getEventTypeIcon(event.event_type)}
                              </Box>
                              <Typography variant="body2" fontWeight={600} sx={{ color: '#2C2416' }}>
                                {getEventTypeLabel(event.event_type)}
                              </Typography>
                            </Box>
                            
                            <Box>
                              <Typography variant="body2" fontWeight={600} sx={{ color: '#2C2416' }}>
                                {new Date(event.event_date).toLocaleDateString('en-IN', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#8B7355', fontSize: '0.75rem' }}>
                                {event.venue || 'TBA'} • {new Date(event.event_date) > new Date() ? 'Upcoming' : 'Past'}
                              </Typography>
                            </Box>
                            
                            <Box>
                              {event.is_published ? (
                                <Chip
                                  label="Published"
                                  color="success"
                                  size="small"
                                  icon={<PublicIcon />}
                                />
                              ) : (
                                <Chip
                                  label="Draft"
                                  color="default"
                                  size="small"
                                  icon={<UnpublishedIcon />}
                                />
                              )}
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Stack>
              ) : (
                /* Desktop Table View */
                <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Title</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Venue</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEvents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" py={3}>
                            No events found. Click "Add Event" to create one.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredEvents.map((event) => (
                        <TableRow key={event.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {event.title}
                            </Typography>
                            {event.description && (
                              <Typography variant="caption" color="text.secondary">
                                {event.description.substring(0, 50)}
                                {event.description.length > 50 ? '...' : ''}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getEventTypeIcon(event.event_type)}
                              <Typography variant="body2">
                                {getEventTypeLabel(event.event_type)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(event.event_date).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(event.event_date) > new Date() ? 'Upcoming' : 'Past'}
                            </Typography>
                          </TableCell>
                          <TableCell>{event.venue || 'TBA'}</TableCell>
                          <TableCell>
                            {event.is_published ? (
                              <Chip
                                label="Published"
                                color="success"
                                size="small"
                                icon={<PublicIcon />}
                              />
                            ) : (
                              <Chip
                                label="Draft"
                                color="default"
                                size="small"
                                icon={<UnpublishedIcon />}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, event)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Event Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingEvent ? 'Edit Event' : 'Add New Event'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Event Title *"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                fullWidth
                placeholder="e.g., Annual Day 2026"
              />

              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Describe the event..."
              />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Event Type *"
                    name="event_type"
                    value={formData.event_type}
                    onChange={handleInputChange}
                    fullWidth
                  >
                    {eventTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Event Date *"
                    name="event_date"
                    type="date"
                    value={formData.event_date}
                    onChange={handleInputChange}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>

              <TextField
                label="Venue"
                name="venue"
                value={formData.venue}
                onChange={handleInputChange}
                fullWidth
                placeholder="e.g., Chinmudra Academy Auditorium"
              />

              <FormControlLabel
                control={
                  <Switch
                    name="is_published"
                    checked={formData.is_published}
                    onChange={handleInputChange}
                  />
                }
                label="Publish Event (visible to students)"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editingEvent ? 'Update' : 'Create'} Event
            </Button>
          </DialogActions>
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={() => {
            handleOpenDialog(selectedEvent);
            handleCloseMenu();
          }}>
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteEvent} sx={{ color: 'error.main' }}>
            Delete
          </MenuItem>
        </Menu>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default Events;
